import React, {ChangeEvent, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  ComputedPageItem,
  HexColour,
  IntColour,
  ShapeShadowPayload,
} from '@taopix/taopix-core/dist/types/types';
import {Checkbox, ContentPanel, DropShadowIcon} from '@taopix/taopix-design-system';
import {RangeWithValue} from '@taopix/taopix-design-system';
import {
  AssetType,
  BoxType,
  ShadowValidator,
  Angle,
  Opacity,
  Colour as ColourUtils,
  BoxTypeShape,
  NumBool,
} from '@taopix/taopix-core';
import {useTaopix} from '../../../Context/TaopixContext';
import classNames from 'classnames';
import {useExperience} from '../../../Experience/ExperienceProvider';
import Colour from './Colour';
import {Tile} from '../../../BackgroundsList/BackgroundsList';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import {getSafeColour} from '../../../../Utils/Colour';
import useSettings from '../../../../Hooks/useSettings';
import ToolPaletteSection from '../../../ToolPalette/ToolPaletteSection';
import PaletteInformationMessage from '../PaletteInformationMessage';

export type ShadowProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

type ShadowExperienceValues = {
  blur: boolean;
  distance: boolean;
  angle: boolean;
  opacity: boolean;
  expand: boolean;
  colour: boolean;
};

const Shadow = ({sectionKey, item}: ShadowProps) => {
  const app = useTaopix();
  const settings = useSettings();
  const ui = useUiState();
  const {t} = useTranslation();

  const {experience} = useExperience();
  const {pictureBoxOptions, shapeBoxOptions, stickerOptions} = experience.EDITOR;

  const [disableShadowEditing, setDisableShadowEditing] = useState<boolean>(
    item?.boxdropshadow?.active === 0
  );

  const [showSection, setShowSection] = useState<boolean>(false);
  const [disableSection, setDisableSection] = useState<boolean>(false);
  const [notAvailableMessage, setNotAvailableMessage] = useState<string>('');

  const [selectedShadowColour, setSelectedShadowColour] = useState<string | number | undefined>(
    getSafeColour(item?.boxdropshadow?.colour as IntColour)
  );

  const colours = settings.state?.colours?.palette;

  const [shadowOptions, setShadowOptions] = useState<ShadowExperienceValues>({
    blur: true,
    distance: true,
    angle: true,
    opacity: true,
    expand: true,
    colour: true,
  });

  // set the inital state from the selected item
  const [shapeShadowValues, setShapeShadowValues] = useState<ShapeShadowPayload>({
    active: item?.boxdropshadow?.active as NumBool,
    alpha: item?.boxdropshadow?.alpha as number,
    angle: app.core.get('utils').normalizeAngle(item?.boxdropshadow?.angle as number),
    blur: item?.boxdropshadow?.blur as number,
    colour: '#000000',
    expand: item?.boxdropshadow?.expand as number,
    offset: item?.boxdropshadow?.offset as number,
  });

  const paletteSection = useRef(ui.state.palette.section);

  useEffect(() => {
    paletteSection.current = ui.state.palette.section;
  }, [ui.state.palette.section]);

  useEffect(() => {
    const colour = ColourUtils.int2Hex(item?.boxdropshadow?.colour as IntColour) as HexColour;
    setShapeShadowValues({
      active: item?.boxdropshadow?.active as NumBool,
      alpha: item?.boxdropshadow?.alpha as number,
      angle: app.core.get('utils').normalizeAngle(item?.boxdropshadow?.angle as number),
      blur: item?.boxdropshadow?.blur as number,
      colour: colour,
      expand: item?.boxdropshadow?.expand as number,
      offset: item?.boxdropshadow?.offset as number,
    });
    setSelectedShadowColour(colour.replace('#', ''));
  }, [item?.ref, item?.boxdropshadow, item?.boxdropshadow?.active]);

  /*
   * TODO - This needs looked at - Changing an item which has a different colour to what
   * is stored will fire an unecessary action
   */
  useEffect(() => {
    if (selectedShadowColour !== '' && selectedShadowColour !== undefined) {
      if (
        item?.ref !== -1 &&
        item?.asset?.assetType !== AssetType.Background &&
        item?.type !== BoxType.Text &&
        item?.contextState.active === true &&
        selectedShadowColour !== getSafeColour(item?.boxdropshadow?.colour as IntColour)
      ) {
        app.core
          .dispatch('SHAPE.UPDATE_SHAPE_SHADOW', {
            colour: `#${selectedShadowColour}`,
          })
          .catch(error => {
            console.error(error);
          });
      }
    }
  }, [selectedShadowColour]);

  const isActiveInExperience = (item: ComputedPageItem) => {
    const boxType = item.type || item.type;
    // Return whether or not this option is enabled in the experience for the selected item
    if (
      boxType === BoxType.Image &&
      item?.asset?.assetType !== AssetType.Background &&
      item?.asset?.assetType !== AssetType.ScrapbookImage
    ) {
      setShadowOptions({
        blur: pictureBoxOptions.blur,
        distance: pictureBoxOptions.distance,
        angle: pictureBoxOptions.angle,
        opacity: pictureBoxOptions.opacity,
        expand: pictureBoxOptions.expand,
        colour: pictureBoxOptions.shadowColour,
      });
      return pictureBoxOptions.shadow;
    }
    if (
      boxType === BoxType.Shape &&
      [BoxTypeShape.Line, BoxTypeShape.Oval, BoxTypeShape.Polygon, BoxTypeShape.Rect].includes(
        item?.shape
      )
    ) {
      setShadowOptions({
        blur: shapeBoxOptions.blur,
        distance: shapeBoxOptions.distance,
        angle: shapeBoxOptions.angle,
        opacity: shapeBoxOptions.opacity,
        expand: shapeBoxOptions.expand,
        colour: shapeBoxOptions.shadowColour,
      });
      return shapeBoxOptions.shadow;
    }
    if (AssetType.ScrapbookImage === item.hiresfiletype) {
      setShadowOptions({
        blur: stickerOptions.blur,
        distance: stickerOptions.distance,
        angle: stickerOptions.angle,
        opacity: stickerOptions.opacity,
        expand: stickerOptions.expand,
        colour: stickerOptions.shadowColour,
      });
      return stickerOptions.shadow;
    } else {
      return false;
    }
  };

  useEffect(() => {
    // If there is a selected item
    if (item && item.ref !== -1) {
      // Show or hide this section based on the relevant experience and advanced editing configuration
      setShowSection(isActiveInExperience(item));
      // Enable or disable this section based on the item's protection
      setDisableSection(
        NumBool.True === item?.protectcontents ||
          (item.errors?.imageMissing as boolean) ||
          ui.state.swapActive
      );
      // Set the message to 'not available' in case the section was hidden or disabled
      setNotAvailableMessage(t('toolpalette:str_OptionNotAvailableForSelectedItem'));
    } else {
      // If no item is selected, hide the section button
      setShowSection(false);
      // And show the 'please select an item' message
      setNotAvailableMessage(t('toolpalette:str_MessageSelectAnItemToViewOptions'));
    }
  }, [
    item?.asset,
    item?.asset?.assetType,
    item?.type,
    item?.image,
    item?.shape,
    ui.state.swapActive,
  ]);

  const sectionButtonClasses = classNames('lg:py-md');

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'px-md',
    'space-y-sm',
    'lg:flex-1',
    'lg:pb-lg',
    'sm:pb-md',
    'sm:h-mobilePaletteContentHeight',
    'sm:max-w-full',
    'overflow-y-auto'
  );

  if (
    ui.state.palette.section !== 'box-settings-palette' &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}` &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}/shadow-colour`
  ) {
    return null;
  }

  return (
    <ToolPaletteSection
      id={'shadowPalette'}
      title={t('boxpalettes:str_TitleShadow')}
      buttonIcon={<DropShadowIcon />}
      sectionKey={sectionKey}
      buttonClasses={sectionButtonClasses}
      useFocusMode
      subSection={true}
      disabled={disableSection}
      showButton={showSection}
      buttonLayout="horizontal"
      parentselected={true}
    >
      <div className={wrapperClasses}>
        {!showSection || disableSection ? (
          // If the palette button has been hidden or disabled (by the experience, box protection or nothing is selected),
          // show the relevant 'unavailable' message in the palette in case the palette was left open when its status changed
          <PaletteInformationMessage message={notAvailableMessage} />
        ) : (
          // Only show the sub options if the palette is allowed in the experience and the content is not protected
          <>
            <Checkbox
              data-testid={'box-settings-shadow-enable-checkbox'}
              id={'enable-shadow-shadow-slider'}
              label={t('boxpalettes:str_LabelEnableShadow')}
              key={`enable-shadow${item?.boxdropshadow?.active === 1}-${item?.ref}`}
              appearance={'checkbox'}
              defaultChecked={item?.boxdropshadow?.active === 1}
              onChange={(isChecked: boolean, event: ChangeEvent) => {
                setDisableShadowEditing(!isChecked);
                if (isChecked) {
                  setShapeShadowValues({
                    ...shapeShadowValues,
                    active: 1,
                  });
                  app.core.dispatch('SHAPE.TOGGLE_SHAPE_SHADOW', {active: 1}).catch(error => {
                    console.error(error);
                  });
                } else {
                  setShapeShadowValues({
                    ...shapeShadowValues,
                    active: 0,
                  });
                  app.core.dispatch('SHAPE.TOGGLE_SHAPE_SHADOW', {active: 0}).catch(error => {
                    console.error(error);
                  });
                }
              }}
            />
            {shapeShadowValues.active === 1 && (
              <>
                {shadowOptions.blur && (
                  <ContentPanel>
                    <RangeWithValue
                      data-testid={'box-settings-shadow-blur-checkbox'}
                      key={`shadow-blur-${item?.ref}`}
                      label={t('boxpalettes:str_TitleBlur')}
                      value={shapeShadowValues.blur}
                      id={'shadow-blur-slider'}
                      min={ShadowValidator.BlurMin}
                      max={ShadowValidator.BlurMax}
                      disabled={disableShadowEditing}
                      submitOnBlur={true}
                      onValueChange={(value: number, interacting?: boolean) => {
                        if (item?.ref && item?.ref !== -1) {
                          app.core
                            .dispatch(
                              'SHAPE.UPDATE_SHAPE_SHADOW',
                              {blur: value},
                              interacting as boolean
                            )
                            .catch(error => {
                              console.error(error);
                            });
                        }
                      }}
                      isSmallScreen={ui.state.isSmallScreen}
                    />
                  </ContentPanel>
                )}

                {shadowOptions.distance && (
                  <ContentPanel>
                    <RangeWithValue
                      key={`shadow-distance-${item?.ref}`}
                      label={t('boxpalettes:str_TitleDistance')}
                      value={shapeShadowValues.offset}
                      id={'shadow-distance-slider'}
                      min={ShadowValidator.DistanceMin}
                      max={ShadowValidator.DistanceMax}
                      disabled={disableShadowEditing}
                      submitOnBlur={true}
                      onValueChange={(value: number, interacting?: boolean) => {
                        if (item?.ref && item?.ref !== -1) {
                          app.core
                            .dispatch('SHAPE.UPDATE_SHAPE_SHADOW', {offset: value}, interacting)
                            .catch(error => {
                              console.error(error);
                            });
                        }
                      }}
                      isSmallScreen={ui.state.isSmallScreen}
                    />
                  </ContentPanel>
                )}

                {shadowOptions.angle && (
                  <ContentPanel>
                    <RangeWithValue
                      key={`shadow-angle-${item?.ref}`}
                      label={t('boxpalettes:str_TitleAngle')}
                      value={shapeShadowValues.angle}
                      id={'shadow-angle-slider'}
                      min={Angle.Min}
                      max={Angle.Max}
                      submitOnBlur={true}
                      onValueChange={(value: number, interacting?: boolean) => {
                        if (item?.ref && item?.ref !== -1) {
                          app.core
                            .dispatch('SHAPE.UPDATE_SHAPE_SHADOW', {angle: value}, interacting)
                            .catch(error => {
                              console.error(error);
                            });
                        }
                      }}
                      disabled={disableShadowEditing}
                      snapToValues={[-180, -135, -90, -45, 0, 45, 90, 135, 180]}
                      showTickMarks
                      isSmallScreen={ui.state.isSmallScreen}
                    />
                  </ContentPanel>
                )}
                {shadowOptions.opacity && (
                  <ContentPanel>
                    <RangeWithValue
                      key={`shadow-opacity-${item?.ref}`}
                      label={t('boxpalettes:str_TitleTransparency')}
                      value={shapeShadowValues.alpha}
                      id={'shadow-opacity-slider'}
                      min={Opacity.Min}
                      max={Opacity.Max}
                      disabled={disableShadowEditing}
                      submitOnBlur={true}
                      onValueChange={(value: number, interacting?: boolean) => {
                        if (item?.ref && item?.ref !== -1) {
                          app.core
                            .dispatch('SHAPE.UPDATE_SHAPE_SHADOW', {alpha: value}, interacting)
                            .catch(error => {
                              console.error(error);
                            });
                        }
                      }}
                      isSmallScreen={ui.state.isSmallScreen}
                    />
                  </ContentPanel>
                )}
                {shadowOptions.expand && (
                  <ContentPanel>
                    <RangeWithValue
                      key={`shadow-expand-${item?.ref}`}
                      label={t('boxpalettes:str_TitleExpand')}
                      value={shapeShadowValues.expand}
                      id={'shadow-expand-slider'}
                      min={ShadowValidator.ExpandMin}
                      max={ShadowValidator.ExpandMax}
                      disabled={disableShadowEditing}
                      submitOnBlur={true}
                      onValueChange={(value: number, interacting?: boolean) => {
                        if (item?.ref && item?.ref !== -1) {
                          app.core
                            .dispatch('SHAPE.UPDATE_SHAPE_SHADOW', {expand: value}, interacting)
                            .catch(error => {
                              console.error(error);
                            });
                        }
                      }}
                      isSmallScreen={ui.state.isSmallScreen}
                    />
                  </ContentPanel>
                )}

                {ui.state.palette.section?.includes('shadow') && shadowOptions.colour && (
                  <ContentPanel>
                    <Colour
                      id={'box-settings-shadow-colour-picker'}
                      parentSection={'shadow'}
                      key={'shadow-colour'}
                      buttonLabel={t('*:str_LabelColour')}
                      sectionTitle={t('boxpalettes:str_TitleShadowColour')}
                      sectionKey={'shadow-colour'}
                      item={item}
                      colours={colours}
                      selectedColour={selectedShadowColour}
                      useFocusMode={ui.state.isSmallScreen}
                      onClick={(
                        event: React.MouseEvent | React.ChangeEvent,
                        newSelectedBackground: Tile
                      ) => {
                        if (selectedShadowColour === newSelectedBackground?.src) {
                          setSelectedShadowColour('000000');
                        } else {
                          setSelectedShadowColour(newSelectedBackground?.src);
                        }
                      }}
                    />
                  </ContentPanel>
                )}
              </>
            )}
          </>
        )}
      </div>
    </ToolPaletteSection>
  );
};

export default Shadow;
